<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useForm, useToast, VaInnerLoading, VaSelect, VaSwitch, VaTextarea } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'
import { validators } from '../../../services/utils'
import { onlyNumbersNoDigits } from '../../../core/helpers'
import { registerContract } from '../../../core/requests/Contracts'
import { getApproved } from '../../../core/requests/Oportunities'
import { getAllServices } from '../../../core/requests/Services'
import { getAllContacts } from '../../../core/requests/Contacts'

const emit = defineEmits(['close'])

const vOnlyNumbers = onlyNumbersNoDigits

const props = defineProps({
  id: {
    default: null,
  },
  data: {
    default: null,
    type: Object,
  },
})

const isLoading = ref(true)
const contractFiles = ref([])
const services: any = ref([])
const allServices: any = ref([])
const contacts: any = ref([])
const { isValid, validate, reset } = useForm('formRef')
const form: any = reactive({
  ...(props.id && { quotation: props.id }),
  payment_frecuence: 1,
})
const { notify } = useToast()
const _quotations: any = ref([])
const selectedQuotation = computed(() => _quotations.value.find((f: any) => f.id === form.quotation))

onMounted(async () => {
  //
  form.from_quotation = !!props.id

  await Promise.all([listServices(), getAllContacts$(), getApproved()]).then(([serv, cont, quotations]: any) => {
    _quotations.value = quotations.filter((f: any) => !f.contract)
    const quotation: any = selectedQuotation.value
    services.value = [
      ...(form.from_quotation
        ? [
            {
              id: 999,
              description: 'Todos',
            },
          ]
        : []),
      ...(quotation?._services || []),
    ]

    isLoading.value = false
  })
})
const getAllContacts$ = async () => {
  const contactsData: any = await getAllContacts()
  contacts.value = contactsData?.map((i: any) => ({
    ...i,
    names: i.fullname,
  }))
}
const listServices = async () => {
  await getAllServices().then((res: any) => {
    allServices.value = res
  })
}
const doProcess = async () => {
  if (!contractFiles.value.length) {
    notify({
      message: 'Debes agregar archivos de contrato',
      color: 'danger',
    })
    return
  }

  if (validate()) {
    isLoading.value = true
    try {
      await registerContract(form, contractFiles.value)
      notify({
        message: 'Contrato registrado correctamente',
        color: 'success',
      })
      emit('close')
      reset()
    } catch (e: any) {
      console.log(e)
    }

    isLoading.value = false
  }
}

const parseAllServices = () => {
  const quotation: any = selectedQuotation.value
  services.value = [
    ...(form.from_quotation
      ? [
          {
            id: 999,
            description: 'Todos',
          },
        ]
      : []),
    ...quotation?._services,
  ]
  form.service = ''
}
const toggleType = () => {
  form.service = ''
  services.value = []
}
const paymentMethods = [
  {
    type: 'Transferencia Bancaria',
    id: 1,
  },
]
</script>

<template>
  <div class="bg-blue-50">
    <div class="mb-4">
      <div class="flex justify-between align-center justify-center">
        <h1 class="h5 font-bold m-0">Nuevo Contrato</h1>
      </div>
    </div>

    <div class="new-customer gap-8">
      <VaForm ref="formRef" immediate class="gap-2">
        <div class="w-full bg-white p-2 mt-3 mb-4 py-5">
          <VaInnerLoading class="gap-2" style="min-width: auto" :loading="isLoading">
            <div class="w-full columns-1">
              <div class="columns-1 mb-3">
                <div class="w-full flex gap-3">
                  <div class="w-1/2">
                    <VaInput
                      v-model="form.title"
                      stateful
                      clearable
                      name="title"
                      label="Nombre de contrato"
                      :rules="[validators.required]"
                    />
                  </div>
                  <div class="w-1/2 flex justify-between">
                    <VaInput
                      v-model="form.start_at"
                      stateful
                      clearable
                      type="date"
                      name="duration"
                      label="Inicio del contrato"
                      class="max-w-[150px]"
                      :rules="[validators.required]"
                    />
                    <VaInput
                      v-model="form.expired_at"
                      stateful
                      clearable
                      type="date"
                      name="duration"
                      :disabled="!form.start_at"
                      :min="form.start_at"
                      class="max-w-[150px]"
                      label="Fin del contrato"
                      :rules="[validators.required]"
                    />
                  </div>
                </div>

                <div class="w-full mt-3 mb-1">
                  <VaTextarea
                    v-model="form.description"
                    class="w-full"
                    stateful
                    clearable
                    name="description"
                    label="Descripción del contrato"
                  />
                </div>
              </div>

              <div class="w-full py-3 pt-0">
                <VaSwitch
                  v-model="form.from_quotation"
                  :disabled="!!props.id"
                  label="¿Tiene cotización?"
                  style="font-size: 11px; font-weight: 600"
                  size="small"
                  @update:modelValue="toggleType"
                />
              </div>

              <div class="gap-3" :class="form.from_quotation ? 'columns-2' : 'columns-1'">
                <div v-if="form.from_quotation">
                  <VaSelect
                    v-model="form.quotation"
                    :disabled="props.id ? true : false"
                    class="w-full pt-1 text-xs"
                    background="#fff"
                    :options="_quotations"
                    value-by="id"
                    text-by="serie_parsed"
                    placeholder="---"
                    searchable
                    label="Selecciona una cotización"
                    search-placeholder-text="Buscar cotización"
                    no-options-text="-- No hay items --"
                    highlight-matched-text
                    :rules="[validators.required]"
                    @update:modelValue="parseAllServices()"
                  />
                </div>

                <div v-if="!form.from_quotation">
                  <VaSelect
                    v-model="form.contact"
                    class="max-w-[250px] pt-1 text-xs"
                    background="#fff"
                    :options="contacts"
                    value-by="id"
                    text-by="names"
                    placeholder="---"
                    searchable
                    clearable
                    no-options-text="-- No hay items --"
                    label="Selecciona un cliente"
                    search-placeholder-text="Buscar cliente"
                    highlight-matched-text
                    :rules="[validators.required]"
                  />
                </div>

                <div class="mt-2">
                  <VaSelect
                    v-model="form.service"
                    :disabled="form.from_quotation ? !selectedQuotation : false"
                    class="w-full pt-1 text-xs"
                    background="#fff"
                    :options="services?.length ? services : allServices"
                    value-by="id"
                    text-by="description"
                    placeholder="---"
                    searchable
                    label="Selecciona el servicio"
                    no-options-text="-- No hay items --"
                    search-placeholder-text="Buscar servicio"
                    highlight-matched-text
                    :rules="[validators.required]"
                  />
                </div>
              </div>

              <div class="columns-3 w-full mt-3">
                <div class="w-full">
                  <VaInput
                    v-model="form.effective_at"
                    v-only-numbers
                    :min="form.start_at"
                    :disabled="!form.start_at"
                    stateful
                    clearable
                    label="Fecha de ejecución"
                    type="date"
                    :rules="[validators.required]"
                  />
                </div>

                <div class="w-full">
                  <VaInput
                    v-model="form.payment_deadline"
                    v-only-numbers
                    :min="form.start_at"
                    :disabled="!form.start_at"
                    stateful
                    clearable
                    label="Fecha de facturación"
                    type="date"
                    :rules="[validators.required]"
                  />
                </div>
                <div class="w-full">
                  <VaSelect
                    v-model="form.payment_frecuence"
                    class="w-full text-xs"
                    background="#fff"
                    :options="paymentMethods"
                    value-by="id"
                    text-by="type"
                    placeholder="---"
                    searchable
                    label="Metodo de pago"
                    no-options-text="-- No hay items --"
                    search-placeholder-text="Buscar metodo"
                    highlight-matched-text
                    :rules="[validators.required]"
                  />
                </div>
              </div>
            </div>
            <br />
            <hr />
            <div class="w-full mt-3">
              <VaFileUpload
                v-model="contractFiles"
                drop-zone-text="Archivos de Contrato"
                dropzone
                upload-button-text="Seleccionar"
                file-incorrect-message="El archivo seleccionado no es un documento PDF o Word"
              />
            </div>
          </VaInnerLoading>
        </div>
      </VaForm>
    </div>

    <div class="pb-3">
      <div class="flex justify-end">
        <div class="flex gap-2">
          <VaButton icon="save" color="primary" :disabled="!isValid" :loading="isLoading" @click="doProcess">
            <span class="text-xs px-1">Nuevo Contrato</span>
          </VaButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.new-customer {
  &-tabs {
    .va-tabs__content {
      width: 100%;
    }
  }

  .float-profile {
    position: absolute;
    top: 0;
    right: 0;
  }
}
</style>
